import React, { useMemo } from 'react';

import { Alert, Box, CircularProgress } from '@mui/material';

import SearchMap from '~/components/Content/GeometrySearch/SearchMap';

import type { PlaceGeom } from '~/hooks/apis/search/useGetPlaces';

const MAP_STYLE = { width: '100%', height: '100%', minHeight: '300px' };
const POLYGON_GEOM_TYPES = ['Polygon', 'MultiPolygon'];

type Props = {
  places: Array<PlaceGeom>;
  isFetching?: boolean;
  isError?: boolean;
};

export default function MatchedPlacesMap({ places, isFetching = false, isError = false }: Props) {
  const { areaMapItems, pointMapItems } = useMemo(() => {
    const areaItems: Array<{ id: string; name: string; geom: NonNullable<PlaceGeom['geom']> }> = [];
    const pointItems: Array<{ id: string; name: string; geom: NonNullable<PlaceGeom['geom']> }> = [];

    places.forEach((place) => {
      if (place.geom == null) {
        return;
      }

      const item = { id: place.placeId, name: place.name, geom: place.geom };

      if (POLYGON_GEOM_TYPES.includes(place.geom.type)) {
        areaItems.push(item);
        return;
      }

      if (place.geom.type === 'Point') {
        pointItems.push(item);
      }
    });

    return { areaMapItems: areaItems, pointMapItems: pointItems };
  }, [places]);

  const hasMapItems = areaMapItems.length > 0 || pointMapItems.length > 0;

  if (!isFetching && !isError && !hasMapItems) {
    return null;
  }

  return (
    <Box sx={{ flex: '2 1 0', minWidth: 0, minHeight: 300, alignSelf: 'stretch' }}>
      {isFetching && <CircularProgress size={20} />}
      {isError && <Alert severity="error">Failed to load matched locations</Alert>}
      {!isFetching && !isError && (
        <SearchMap areaMapItems={areaMapItems} pointMapItems={pointMapItems} containerStyle={MAP_STYLE} />
      )}
    </Box>
  );
}
